import { useTdsCommon } from '../../hooks/useTdsCommon'
import { getStateCode, formatDate, formatDecimalAmount, padString, padNumber, reverseStateCodes, getTdsFormRoute } from '../../utils/tdsCommon'
import { getStorageItem, setStorageItem } from '../../utils/storage'
import { extractProfileDataApi } from '../../utils/tdsFileHandlers'
import { stateCodes } from '../../utils/tdsCommon'
import TdsProfileSection from '../../components/dashboard/TdsProfileSection'
import { getLegacyStorageKey } from '../../utils/storage'
import React, { useState, useEffect, useCallback, useRef } from 'react'

import { motion } from 'framer-motion'
import { FileText, User, Upload, ArrowLeft, Building2, X, Trash2, Eye, EyeOff } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import axios from 'axios'
import TdsDetailsTable from '../../components/dashboard/TdsDetailsTable'
import ChallanDetailsTable from '../../components/dashboard/ChallanDetailsTable'
import TdsSidebar from '../../components/dashboard/TdsSidebar'
import TdsGeneratedFiles from './TdsGeneratedFiles'
import { useError } from '../../contexts/ErrorContext'
import { form140Sections, minorHeadCodes140, deductorTypes140 } from '../../utils/tds140Constants'

const stateOptions = [
  'ANDAMAN AND NICOBAR ISLANDS', 'ANDHRA PRADESH', 'ARUNACHAL PRADESH', 'ASSAM', 'BIHAR', 'CHANDIGARH',
  'CHHATTISGARH', 'DADRA & NAGAR HAVELI AND DAMAN & DIU', 'DELHI', 'GOA', 'GUJARAT', 'HARYANA',
  'HIMACHAL PRADESH', 'JAMMU & KASHMIR', 'JHARKHAND', 'KARNATAKA', 'KERALA', 'LADAKH', 'LAKSHWADEEP',
  'MADHYA PRADESH', 'MAHARASHTRA', 'MANIPUR', 'MEGHALAYA', 'MIZORAM', 'NAGALAND', 'ODISHA',
  'PONDICHERRY', 'PUNJAB', 'RAJASTHAN', 'SIKKIM', 'TAMIL NADU', 'TELANGANA', 'TRIPURA',
  'UTTAR PRADESH', 'UTTARAKHAND', 'WEST BENGAL'
];

const deducteeCodes140 = [
  { value: '1', label: '01 - Companies' },
  { value: '2', label: '02 - Other than Companies' }
];

const reasonCodes140 = [
  { value: '', label: 'None' },
  { value: 'A', label: 'A - Lower/No deduction on account of certificate u/s 395(1)' },
  { value: 'B', label: 'B - No deduction on account of declaration u/s 393(6)' },
  { value: 'C', label: 'C - Higher rate u/s 397(2) on account of non-furnishing of PAN' },
  { value: 'D', label: 'D - No/lower deduction on account of payment made to specific person' },
  { value: 'E', label: 'E - No deduction on account of payment referred to in Board Circulars' },
  { value: 'N', label: 'N - No deduction under section 393(3) [Table: Sl. No. 5.D(a)/(b)]' },
  { value: 'O', label: 'O - No deduction as per provisions of section 393(4)' },
  { value: 'P', label: 'P - No deduction on dividend paid to business trust u/s 393(4)' },
  { value: 'Q', label: 'Q - No deduction on payment made to entity u/s 393(4)' },
  { value: 'S', label: 'S - No deduction in view of tax deductible/collectible under other provision' },
  { value: 'T', label: 'T - No deduction on account of deductee/payee' },
  { value: 'Y', label: 'Y - No deduction on account of payment below threshold limit' },
  { value: 'Z', label: 'Z - No/lower deduction in view of notification u/s 400(1)' }
];

const Tds140CorrectionForm = ({ isMobile: isMobileProp, onSidebarStateChange }) => {
  const csiFileInputRef = useRef(null);
  console.log('Rendering Tds140CorrectionForm component');
  const navigate = useNavigate()
  const { addError } = useError()
  const [activeSection, setActiveSection] = useState('profile')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(isMobileProp || (typeof window !== 'undefined' ? window.innerWidth < 768 : false))

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const [sidebarState, setSidebarState] = useState({
    isOpen: true,
    isExpanded: true,
    keepOpen: true,
    hovered: false
  })

  const {
    profileData,
    challanData,
    tdsData,
    originalTdsData,
    csiFile,
    consoFileContent,
    isValidating,
    showCsiUploadPopup,
    showDownloadPrompt,
    selectedFinancialYear,
    selectedQuarter,
    selectedForm,
    selectedReturnType,
    challanDateRange,
    setProfileData,
    setChallanData,
    setTdsData,
    setOriginalTdsData,
    setCsiFile,
    setConsoFileContent,
    setIsValidating,
    setShowCsiUploadPopup,
    setShowDownloadPrompt,
    setSelectedFinancialYear,
    setSelectedQuarter,
    setSelectedForm,
    setSelectedReturnType,
    handlePrepareReturnFile,
    handleValidateReturn,
    handleSkipDownload,
    handleCancelValidation,
    executeValidation
  } = useTdsCommon({
    formName: 'tds',
    defaultFormType: '140',
    isCorrection: true,
    generateTdsStatementFile,
    validateDataForReturn,
    onValidationError: (errors) => {
      addError('Please fix the following errors before preparing return:')
      errors.forEach(err => addError(`• ${err}`))
      setSidebarOpen(true)
      setActiveSection('errors')
    }
  });

  const [isProfileEditing, setIsProfileEditing] = useState(true)
  const [downloadPassword, setDownloadPassword] = useState('')
  const [showDownloadPassword, setShowDownloadPassword] = useState(false)
  const [isDownloadingCsi, setIsDownloadingCsi] = useState(false)

  const handleCsiDownload = async () => {
    if (!downloadPassword) {
      addError('Password is required');
      return;
    }

    setIsDownloadingCsi(true);
    try {
      const response = await axios.post('/api/login-income-tax', {
        credentials: {
          tan: profileData?.section1?.taxDeduction,
          password: downloadPassword
        },
        dateRange: challanDateRange
      });

      if (response.data.success && response.data.csiContent) {
        toast.success('CSI File Downloaded Successfully!');
        setShowDownloadPrompt(false);
        setDownloadPassword('');

        const binaryString = window.atob(response.data.csiContent.replace(/\s/g, ''));
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const file = new File([bytes], response.data.fileName || 'downloaded.csi', { type: 'text/plain' });
        if (typeof setCsiFile === 'function') setCsiFile(file);
        if (typeof executeValidation === 'function') executeValidation(file);
      } else {
        const errMsg = response.data.error || 'Failed to download CSI file. Please check password.';
        toast.error(errMsg);
      }
    } catch (err) {
      console.error(err);
      addError('Failed to initiate download');
    } finally {
      setIsDownloadingCsi(false);
    }
  };

  // Profile Pre-fill Automation State
  const [showPrefillPrompt, setShowPrefillPrompt] = useState(false);
  const [prefillPassword, setPrefillPassword] = useState('');
  const [showPrefillPassword, setShowPrefillPassword] = useState(false);
  const [isExtractingProfile, setIsExtractingProfile] = useState(false)

  const handleProfilePrefill = async () => {
    if (!prefillPassword) {
      toast.error('Portal password is required');
      return;
    }

    setIsExtractingProfile(true);
    try {
      const { success, data, error } = await extractProfileDataApi({
        tan: profileData?.section1?.taxDeduction,
        password: prefillPassword
      });

      if (success && data) {
        const newData = { ...profileData };
        if (data.pan) {
          if (newData.section1) newData.section1.permanentAccountNumber = data.pan;
          if (newData.section3) newData.section3.permanentAccountNumber = data.pan;
        }
        if (data.name) {
          if (newData.section2) newData.section2.name = data.name;
        }
        if (data.keyPerson) {
          if (data.keyPerson.name && newData.section3) newData.section3.name = data.keyPerson.name;
          if (data.keyPerson.pan && newData.section3) newData.section3.permanentAccountNumber = data.keyPerson.pan;
        }
        if (data.email) {
          if (newData.section2) newData.section2.email = data.email;
          if (newData.section3) newData.section3.email = data.email;
        }
        if (data.mobile) {
          if (newData.section2) newData.section2.mobileNo = data.mobile;
          if (newData.section3) newData.section3.mobileNo = data.mobile;
        }
        if (data.address) {
          if (newData.section2) {
            newData.section2.flatNo = data.address.flatNo || '';
            newData.section2.nameOfPremises = data.address.premisesName || '';
            newData.section2.roadStreet = data.address.roadStreet || '';
            newData.section2.areaLocality = data.address.areaLocality || '';
            newData.section2.townCity = data.address.city || '';
            newData.section2.pinCode = data.address.pinCode || '';
            if (data.address.state) {
              newData.section2.state = data.address.state.toUpperCase().trim();
            }
          }
        }

        if (typeof setProfileData === 'function') {
          setProfileData(newData);
        }
        toast.success('Profile Data Auto-Filled Successfully!');
        setShowPrefillPrompt(false);
        setPrefillPassword('');
      } else {
        toast.error(error || 'Failed to extract profile data');
      }
    } catch (err) {
      console.error(err);
      toast.error('An error occurred during extraction');
    } finally {
      setIsExtractingProfile(false);
    }
  };

  const [originalProfileData, setOriginalProfileData] = useState(null)
  const [originalChallanData, setOriginalChallanData] = useState([])
  const [batchNumber, setBatchNumber] = useState('1')
  const [consoFhHash, setConsoFhHash] = useState('')

  useEffect(() => {
    (async () => {
      const savedOriginalChallanData = await getStorageItem('tds140OriginalChallanData')
      const savedOriginalProfileData = await getStorageItem('tds140OriginalProfileData')
      const savedBatchNumber = localStorage.getItem('tds140BatchNumber')
      if (savedOriginalChallanData) setOriginalChallanData(savedOriginalChallanData)
      if (savedOriginalProfileData) setOriginalProfileData(savedOriginalProfileData)
      if (savedBatchNumber) setBatchNumber(savedBatchNumber)
    })()
  }, [])

  const handleChallanDataChange = setChallanData;
  const handleTdsDataChange = setTdsData;

  const handleSectionChange = (section) => {
    setActiveSection(section)
  }

  const handleSidebarClose = () => {
    setSidebarOpen(false)
  }

  const handleSidebarStateChange = useCallback((state) => {
    setSidebarState(state)
  }, [])

  // Sync Section 3 address fields with Section 2 when "Same as above" is checked
  useEffect(() => {
    if (
      profileData?.section3?.sameAsAbove &&
      isProfileEditing &&
      profileData?.section2 &&
      profileData?.section3
    ) {
      const newData = { ...profileData }
      if (!newData.section2) newData.section2 = {}
      if (!newData.section3) newData.section3 = {}

      newData.section3.nameOfPremises = newData.section2.nameOfPremises || ''
      newData.section3.flatNo = newData.section2.flatNo || ''
      newData.section3.areaLocality = newData.section2.areaLocality || ''
      newData.section3.roadStreet = newData.section2.roadStreet || ''
      newData.section3.townCity = newData.section2.townCity || ''
      newData.section3.pinCode = newData.section2.pinCode || ''
      newData.section3.state = newData.section2.state || ''
      newData.section3.email = newData.section2.email || ''
      newData.section3.stdCode = newData.section2.stdCode || ''
      newData.section3.telephone = newData.section2.telephoneNo || ''
      setProfileData(newData)
    }
  }, [
    profileData?.section2?.nameOfPremises,
    profileData?.section2?.flatNo,
    profileData?.section2?.areaLocality,
    profileData?.section2?.roadStreet,
    profileData?.section2?.townCity,
    profileData?.section2?.pinCode,
    profileData?.section2?.state,
    profileData?.section2?.email,
    profileData?.section2?.stdCode,
    profileData?.section2?.telephoneNo,
    profileData?.section3?.sameAsAbove,
    isProfileEditing
  ])

  // Profile form handlers
  const handleProfileInputChange = (field, value) => {
    const keys = field.split('.')
    const newData = { ...profileData }

    if (!newData.section1) newData.section1 = {}
    if (!newData.section2) newData.section2 = {}
    if (!newData.section3) newData.section3 = {}

    let current = newData
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {}
      }
      current = current[keys[i]]
    }
    current[keys[keys.length - 1]] = value

    if (field === 'section3.sameAsAbove' && value === true && newData.section2 && newData.section3) {
      newData.section3.nameOfPremises = newData.section2.nameOfPremises || ''
      newData.section3.flatNo = newData.section2.flatNo || ''
      newData.section3.areaLocality = newData.section2.areaLocality || ''
      newData.section3.roadStreet = newData.section2.roadStreet || ''
      newData.section3.townCity = newData.section2.townCity || ''
      newData.section3.pinCode = newData.section2.pinCode || ''
      newData.section3.state = newData.section2.state || ''
      newData.section3.email = newData.section2.email || ''
      newData.section3.stdCode = newData.section2.stdCode || ''
      newData.section3.telephone = newData.section2.telephoneNo || ''
    }
    setProfileData(newData)
  }

  // Validation function for Prepare Return
  function validateDataForReturn() {
    const errors = []

    if (!tdsData || tdsData.length === 0) {
      errors.push('At least one TDS entry is required')
    }

    if (!challanData || challanData.length === 0) {
      errors.push('At least one Challan entry is required')
    }

    if (selectedReturnType === 'Correction') {
      if (!profileData?.section1?.receiptNoOriginal) {
        errors.push('Receipt No. of Original Return is missing (Upload valid Conso File)')
      }
      if (!profileData?.section1?.receiptNoEarlier) {
        errors.push('Receipt No. of Earlier Statement is missing (Upload valid Conso File)')
      }
    }

    if (!profileData?.section2?.name) {
      errors.push('Name is required in Profile Section 2')
    }
    if (!profileData?.section2?.flatNo) {
      errors.push('Flat No. is required in Profile Section 2')
    }
    if (!profileData?.section2?.state) {
      errors.push('State is required in Profile Section 2')
    }
    if (!profileData?.section2?.pinCode) {
      errors.push('PIN Code is required in Profile Section 2')
    }
    if (!profileData?.section3?.name) {
      errors.push('Responsible Person Name is required in Profile')
    }
    if (!profileData?.section3?.designation) {
      errors.push('Responsible Person Designation is required in Profile')
    }
    if (!profileData?.section3?.flatNo && !profileData?.section3?.nameOfPremises && !profileData?.section3?.roadStreet) {
      errors.push('Responsible Person Address is required in Profile')
    }
    if (!profileData?.section3?.state) {
      errors.push('Responsible Person State is required in Profile')
    }
    if (!profileData?.section3?.pinCode) {
      errors.push('Responsible Person Pincode is required in Profile')
    }
    if (!profileData?.section3?.email) {
      errors.push('Responsible Person Email is required in Profile')
    }
    if (!profileData?.section3?.mobileNo) {
      errors.push('Responsible Person Mobile is required in Profile')
    }
    if (!profileData?.section3?.permanentAccountNumber) {
      errors.push('Responsible Person PAN is required in Profile')
    }

    if (tdsData && tdsData.length > 0) {
      tdsData.forEach((row, index) => {
        if (!row.panOfDeductee) {
          errors.push(`TDS Entry ${index + 1}: PAN of Deductee is required`)
        }
        if (!row.sectionCode) {
          errors.push(`TDS Entry ${index + 1}: Section Code is required`)
        }
        if (row.tdsAmount === '' || row.tdsAmount === null || row.tdsAmount === undefined) {
          row.tdsAmount = 0;
        }
        if (!row.dateOfPayment) {
          errors.push(`TDS Entry ${index + 1}: Date of Payment is required`)
        }
        if (!row.dateOfDeduction && (parseFloat(row.tdsAmount || 0) + parseFloat(row.surcharge || 0) + parseFloat(row.cess || 0)) > 0) {
          errors.push(`TDS Entry ${index + 1}: Date of Deduction is required`)
        }
        if (parseFloat(row.tdsAmount) > 0 && !row.challanId) {
          errors.push(`TDS Entry ${index + 1}: Challan Selection is required`)
        }
      })
    }

    if (challanData && challanData.length > 0) {
      challanData.forEach((row, index) => {
        if (!row.tdsAmount || parseFloat(row.tdsAmount) <= 0) {
          errors.push(`Challan Entry ${index + 1}: TDS Amount is required`)
        }
        if (!row.dateOfDeposit) {
          errors.push(`Challan Entry ${index + 1}: Date of Deposit is required`)
        }
        if (!row.bsrCode) {
          errors.push(`Challan Entry ${index + 1}: BSR Code is required`)
        } else if (row.bsrCode.length !== 7) {
          errors.push(`Challan Entry ${index + 1}: BSR Code must be 7 digits`)
        }
        if (!row.challanSerialNo) {
          errors.push(`Challan Entry ${index + 1}: Challan Serial No. is required`)
        } else if (row.challanSerialNo.length !== 5) {
          errors.push(`Challan Entry ${index + 1}: Challan Serial No. must be 5 digits`)
        }
      })
    }

    return errors
  }

  // File generation function for Form 140 Correction
  function generateTdsStatementFile() {
    const currentDate = new Date()
    const dd = String(currentDate.getDate()).padStart(2, '0')
    const mm = String(currentDate.getMonth() + 1).padStart(2, '0')
    const yyyy = currentDate.getFullYear()
    const fileCreationDate = `${dd}${mm}${yyyy}`

    let fyStartYear = 0
    let financialYear = ''

    if (selectedFinancialYear) {
      const parts = selectedFinancialYear.split('-')
      if (parts.length >= 2) {
        fyStartYear = parseInt(parts[0], 10)
        const yearPart2 = parts[1].length === 4 ? parts[1].slice(-2) : parts[1]
        financialYear = `${parts[0]}${yearPart2}`
      }
    }

    if (!fyStartYear || isNaN(fyStartYear)) {
      const today = new Date()
      fyStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1
      financialYear = `${fyStartYear}${String(fyStartYear + 1).slice(-2)}`
    }

    const ayStart = fyStartYear + 1
    const assessmentYear = `${ayStart}${String(ayStart + 1).slice(-2)}`

    const formatMinorHead = (val) => {
      if (!val) return ''
      const s = String(val).trim()
      if (s === '200' || s === '400' || s === '100') return s
      const lower = s.toLowerCase()
      if (lower.includes('payable') || lower.includes('taxpayer')) return '200'
      if (lower.includes('regular') || lower.includes('assessment')) return '400'
      if (lower.includes('advance')) return '100'
      const match = s.match(/(200|400|100)/)
      if (match) return match[0]
      return s
    }

    const formatRate4Decimals = (val) => {
      const n = parseFloat(val || 0)
      return n.toFixed(4)
    }

    const isBhChanged = () => {
      if (!originalProfileData) return false;
      const s1Keys = ['typeOfDeduction'];
      const s2Keys = ['name', 'branchDivision', 'flatNo', 'nameOfPremises', 'roadStreet', 'areaLocality', 'townCity', 'pinCode', 'state', 'email', 'stdCode', 'telephoneNo'];
      const s3Keys = ['name', 'designation', 'flatNo', 'nameOfPremises', 'roadStreet', 'areaLocality', 'townCity', 'pinCode', 'state', 'email', 'mobileNo', 'telephone', 'permanentAccountNumber'];

      const changed1 = s1Keys.some(k => (profileData.section1?.[k] || '') !== (originalProfileData.section1?.[k] || ''));
      const changed2 = s2Keys.some(k => (profileData.section2?.[k] || '') !== (originalProfileData.section2?.[k] || ''));
      const changed3 = s3Keys.some(k => (profileData.section3?.[k] || '') !== (originalProfileData.section3?.[k] || ''));

      return changed1 || changed2 || changed3;
    }

    let fileContent = ''
    let lineNumber = 1

    const section1 = profileData?.section1 || {}
    const section2 = profileData?.section2 || {}
    const section3 = profileData?.section3 || {}

    const normalizeDate = (d) => {
      if (!d) return ''
      const s = String(d).trim().replace(/[-/]/g, '')
      if (/^\d{8}$/.test(s)) {
        if (s.startsWith('20') || s.startsWith('19')) {
          return `${s.slice(6, 8)}${s.slice(4, 6)}${s.slice(0, 4)}`
        }
        return s
      }
      return s
    }

    const normalizeMinorHead = (mh) => {
      if (!mh) return '200'
      const str = String(mh).trim()
      if (str.startsWith('400')) return '400'
      return '200'
    }

    const normalizeSerial = (s) => {
      if (!s) return ''
      return String(s).trim().replace(/^0+/, '')
    }

    const normalizeBsr = (b) => {
      if (!b) return ''
      return String(b).trim()
    }

    const isAmountDifferent = (a, b) => {
      const n1 = parseFloat(a || 0)
      const n2 = parseFloat(b || 0)
      return Math.abs(n1 - n2) > 0.01
    }

    const getOriginalDeductee = (tds) => {
      if (originalTdsData && originalTdsData.length > 0) {
        if (tds.id) {
          const byId = originalTdsData.find(o => o.id && String(o.id) === String(tds.id))
          if (byId) return byId
        }
        if (tds.recordNo) {
          const byRecordNo = originalTdsData.find(o =>
            o.recordNo && String(o.recordNo) === String(tds.recordNo) && String(o.challanId) === String(tds.challanId)
          )
          if (byRecordNo) return byRecordNo
        }
      }
      if (tds.source === 'conso' || tds.originalPan) {
        return {
          panOfDeductee: tds.originalPan || tds.panOfDeductee,
          panOfCollectee: tds.originalPan || tds.panOfCollectee,
          nameOfDeductee: tds.nameOfDeductee,
          amountOfPayment: tds.amountOfPayment,
          dateOfPayment: tds.dateOfPayment,
          dateOfDeduction: tds.dateOfDeduction,
          rateOfTax: tds.rateOfTax,
          tdsAmount: tds.originalTotalTax !== undefined ? tds.originalTotalTax : tds.tdsAmount,
          sectionCode: tds.sectionCode,
          reasonForNonDeduction: tds.reasonForNonDeduction,
          certificateNo197: tds.certificateNo197,
          uinForm121: tds.uinForm121,
          recordNo: tds.recordNo,
          source: 'conso'
        }
      }
      return null
    }

    const getOriginalChallan = (challan) => {
      if (originalChallanData && originalChallanData.length > 0) {
        if (challan.id) {
          const byId = originalChallanData.find(c => c.id && String(c.id) === String(challan.id))
          if (byId) return byId
        }
        const bsr = normalizeBsr(challan.originalBsrCode || challan.bsrCode)
        const serial = normalizeSerial(challan.originalChallanNo || challan.challanSerialNo)
        if (bsr && serial) {
          const byBsrSerial = originalChallanData.find(c =>
            normalizeBsr(c.bsrCode) === bsr && normalizeSerial(c.challanSerialNo) === serial
          )
          if (byBsrSerial) return byBsrSerial
        }
      }
      if (challan.source === 'conso' || challan.originalChallanNo || challan.originalBsrCode) {
        return {
          challanSerialNo: challan.originalChallanNo || challan.challanSerialNo,
          bsrCode: challan.originalBsrCode || challan.bsrCode,
          dateOfDeposit: challan.originalDateOfDeposit || challan.dateOfDeposit,
          minorHead: challan.originalMinorHead || challan.minorHead || '200',
          tdsAmount: challan.originalTdsAmount !== undefined ? challan.originalTdsAmount : challan.tdsAmount,
          totalAmount: challan.originalTotalAmount !== undefined ? challan.originalTotalAmount : challan.totalAmount,
          interest: challan.originalInterest !== undefined ? challan.originalInterest : challan.interest,
          feesAmount: challan.originalFeesAmount !== undefined ? challan.originalFeesAmount : challan.feesAmount,
          other: challan.originalOther !== undefined ? challan.originalOther : challan.other,
          bookEntry: challan.originalBookEntry !== undefined ? challan.originalBookEntry : challan.bookEntry,
          recordNo: challan.recordNo,
          source: 'conso'
        }
      }
      return null
    }

    // STEP 1: Classify deductees
    const isPanChanged = (tds) => {
      const original = getOriginalDeductee(tds)
      if (!original) return false
      if (tds.consoMode === 'F') return false
      const curPan = String(tds.panOfDeductee || tds.panOfCollectee || '').trim().toUpperCase()
      const origPan = String(original.panOfDeductee || original.panOfCollectee || '').trim().toUpperCase()
      return curPan !== '' && origPan !== '' && curPan !== origPan
    }

    const isOtherDdsChanged = (tds) => {
      const original = getOriginalDeductee(tds)
      if (!original) return true // Newly added deductee

      const curPayDate = normalizeDate(tds.dateOfPayment)
      const origPayDate = normalizeDate(original.dateOfPayment)
      if (curPayDate && origPayDate && curPayDate !== origPayDate) return true

      const curDedDate = normalizeDate(tds.dateOfDeduction)
      const origDedDate = normalizeDate(original.dateOfDeduction)
      if (curDedDate && origDedDate && curDedDate !== origDedDate) return true

      if (isAmountDifferent(tds.amountOfPayment, original.amountOfPayment)) return true
      if (isAmountDifferent(tds.tdsAmount, original.tdsAmount)) return true
      if (isAmountDifferent(tds.rateOfTax, original.rateOfTax)) return true

      const strFields = [
        'nameOfDeductee', 'sectionCode', 'reasonForNonDeduction', 'certificateNo197', 'uinForm121'
      ]
      for (const k of strFields) {
        const v1 = tds[k] !== undefined && tds[k] !== null ? String(tds[k]).trim() : ''
        const v2 = original[k] !== undefined && original[k] !== null ? String(original[k]).trim() : ''
        if (v1 !== '' && v2 !== '' && v1 !== v2) return true
      }
      return false
    }

    const isChallanModified = (challan) => {
      const original = getOriginalChallan(challan)
      if (!original) return false // Not modified if it has no baseline; truly new challans go to C9

      const curSerial = normalizeSerial(challan.challanSerialNo)
      const origSerial = normalizeSerial(original.challanSerialNo)
      if (curSerial && origSerial && curSerial !== origSerial) return true

      const curBsr = normalizeBsr(challan.bsrCode)
      const origBsr = normalizeBsr(original.bsrCode)
      if (curBsr && origBsr && curBsr !== origBsr) return true

      const curDate = normalizeDate(challan.dateOfDeposit)
      const origDate = normalizeDate(original.dateOfDeposit)
      if (curDate && origDate && curDate !== origDate) return true

      const curMh = normalizeMinorHead(challan.minorHead)
      const origMh = normalizeMinorHead(original.minorHead)
      if (curMh !== origMh) return true

      if (Boolean(challan.bookEntry) !== Boolean(original.bookEntry)) return true

      if (isAmountDifferent(challan.tdsAmount, original.tdsAmount)) return true
      if (isAmountDifferent(challan.interest, original.interest)) return true
      if (isAmountDifferent(challan.feesAmount, original.feesAmount)) return true
      if (isAmountDifferent(challan.other, original.other)) return true

      const curIntAlloc = challan.interestAllocation !== undefined && challan.interestAllocation !== '' ? challan.interestAllocation : (challan.interest || 0)
      const origIntAlloc = original.interestAllocation !== undefined && original.interestAllocation !== '' ? original.interestAllocation : (original.interest || 0)
      if (isAmountDifferent(curIntAlloc, origIntAlloc)) return true

      const curFeesAlloc = challan.feesAllocation !== undefined && challan.feesAllocation !== '' ? challan.feesAllocation : (challan.otherAllocation !== undefined && challan.otherAllocation !== '' ? challan.otherAllocation : (challan.feesAmount || challan.other || 0))
      const origFeesAlloc = original.feesAllocation !== undefined && original.feesAllocation !== '' ? original.feesAllocation : (original.otherAllocation !== undefined && original.otherAllocation !== '' ? original.otherAllocation : (original.feesAmount || original.other || 0))
      if (isAmountDifferent(curFeesAlloc, origFeesAlloc)) return true

      return false
    }

    const checkIsNewChallan = (challan) => {
      if (challan.source === 'conso' || challan.originalChallanNo || challan.originalBsrCode) return false
      return !getOriginalChallan(challan)
    }

    // STEP 2: Group by challans
    const challanGroups = challanData.map(challan => {
      const allDDs = tdsData.filter(t => t.challanId == challan.id)
      const panDDs = allDDs.filter(isPanChanged)
      const otherDDs = allDDs.filter(isOtherDdsChanged)
      const changedDDs = allDDs.filter(d => isPanChanged(d) || isOtherDdsChanged(d))
      const challanChanged = isChallanModified(challan)
      const isNew = checkIsNewChallan(challan)

      return {
        challan,
        allDDs,
        panDDs,
        otherDDs,
        changedDDs,
        challanChanged,
        isNewChallan: isNew
      }
    })

    // STEP 3: Determine batches (C5, C9, C3, C2, C1)
    const c5Groups = challanGroups.filter(g => g.panDDs.length > 0)
    const c9Groups = challanGroups.filter(g => g.isNewChallan)
    const c3Groups = challanGroups.filter(g => !g.isNewChallan && g.changedDDs.length > 0)
    const c2Groups = challanGroups.filter(g => !g.isNewChallan && g.changedDDs.length === 0 && g.challanChanged)
    const bhChanged = isBhChanged()

    const batches = []

    if (c5Groups.length > 0) {
      // 1. PAN updation batch
      batches.push({ type: 'C5', groups: c5Groups })

      // 2. If new challans exist, C9 batch
      if (c9Groups.length > 0) {
        batches.push({ type: 'C9', groups: c9Groups })
      }

      // 3. If BH, Challan, or other non-PAN DD changes exist, add C3 batch including all changed deductees (treating PAN updation also as deductee change)
      const hasOtherChanges = bhChanged || c2Groups.length > 0 || challanGroups.some(g => g.otherDDs.length > 0)
      if (hasOtherChanges) {
        const combinedC3Groups = challanGroups.filter(g => !g.isNewChallan && (g.changedDDs.length > 0 || g.challanChanged))
        batches.push({ type: 'C3', groups: combinedC3Groups })
      }
    } else {
      // Standalone scenarios (No PAN update)
      if (c9Groups.length > 0) {
        batches.push({ type: 'C9', groups: c9Groups })
      }
      if (c3Groups.length > 0) {
        // DD record changes (can include Challan changes & BH changes) -> C3
        const combinedC3Groups = [...c3Groups, ...c2Groups]
        batches.push({ type: 'C3', groups: combinedC3Groups })
      } else if (c2Groups.length > 0) {
        // Challan details changes (and/or BH changes), NO DD changes -> C2
        batches.push({ type: 'C2', groups: c2Groups })
      } else if (bhChanged) {
        // ONLY Deductor (BH) changes -> C1
        batches.push({ type: 'C1', groups: [] })
      }
    }

    if (batches.length === 0) {
      batches.push({ type: 'C1', groups: [] })
    }
    const totalBatches = batches.length

    // 1. File Header Record (FH)
    const fh = Array(19).fill('')
    fh[1] = lineNumber                   // 1. Line Number
    fh[2] = 'FH'                         // 2. Record Type
    fh[3] = 'NS1'                        // 3. File Type for FN 140
    fh[4] = 'C'                          // 4. Upload Type
    fh[5] = fileCreationDate             // 5. File Creation Date
    fh[6] = '1'                          // 6. File Sequence No.
    fh[7] = 'D'                          // 7. Uploader Type
    fh[8] = section1.taxDeduction || ''  // 8. TAN of Deductor
    fh[9] = String(totalBatches)         // 9. Total No. of Batches
    fh[10] = 'Tax Eases Client'           // 10. Name of RPU
    fh[11] = ''                          // 11. Record Hash
    fh[12] = ''                          // 12. FVU Version
    fh[13] = ''                          // 13. File Hash
    fh[14] = ''                          // 14. Sam Version
    fh[15] = ''                          // 15. SAM Hash
    fh[16] = ''                          // 16. SCM Version
    fh[17] = ''                          // 17. SCM Hash
    fh[18] = consoFhHash || ''           // 18. Consolidated File Hash
    fileContent += fh.slice(1, 19).join('^') + '\n'; lineNumber++;

    // Helper to build BH record for Form 140
    const buildBH = (batchNo, corrType, includesBhChanges, batchTotalAmt) => {
      const dAddr1 = `${section2.flatNo || ''} ${section2.nameOfPremises || ''} ${section2.roadStreet || ''}`.trim()
      const rAddr1 = `${section3.flatNo || ''} ${section3.nameOfPremises || ''} ${section3.roadStreet || ''}`.trim()
      const bh = Array(73).fill('')
      const updInd = includesBhChanges ? '1' : '0'

      bh[1] = lineNumber                       // 1. Line Number
      bh[2] = 'BH'                             // 2. Record Type
      bh[3] = String(batchNo)                  // 3. Batch Number
      bh[4] = ''                               // 4. Record Count (Placeholder)
      bh[5] = '140'                            // 5. Form Number (Fixed 140)
      bh[6] = corrType                         // 6. Transaction Type (C1, C2, C3, C5, C9)
      bh[7] = (corrType === 'C1' || corrType === 'C5' || corrType === 'C9') ? '' : updInd // 7. Batch Updation Indicator
      bh[8] = section1.receiptNoOriginal || '' // 8. Original PRN Token Number
      bh[9] = section1.receiptNoEarlier || section1.receiptNoOriginal || '' // 9. Previous PRN Token Number
      bh[10] = ''                              // 10. Token Number
      bh[11] = ''                              // 11. Date of Token Number
      bh[12] = section1.taxDeduction || ''     // 12. Last TAN of Deductor
      bh[13] = (corrType === 'C5') ? '' : (section1.taxDeduction || '') // 13. TAN of Deductor
      bh[14] = ''                              // 14. Receipt number
      bh[15] = (corrType === 'C5' || corrType === 'C9') ? '' : (updInd === '1' || corrType === 'C1' ? (section1.permanentAccountNumber || 'PANNOTREQD') : '') // 15. PAN of Deductor
      bh[16] = assessmentYear                  // 16. Assessment Year
      bh[17] = financialYear                   // 17. Tax Year
      bh[18] = selectedQuarter                 // 18. Period (Q1, Q2, Q3, Q4)
      bh[19] = padString(section2.name, 75)    // 19. Name of Deductor
      bh[20] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? padString(section2.countryRegion || 'INDIA', 25) : '' // 20. Deductor Address 6 (Country)
      bh[21] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? padString(dAddr1, 25) : '' // 21. Deductor Address 1
      bh[22] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? padString(section2.nameOfPremises || section2.areaLocality || '', 25) : '' // 22. Deductor Address 2
      bh[23] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? padString(section2.roadStreet || section2.townCity || '', 25) : '' // 23. Deductor Address 3
      bh[24] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? padString(section2.areaLocality || '', 25) : '' // 24. Deductor Address 4
      bh[25] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? padString(section2.townCity || '', 25) : '' // 25. Deductor Address 5
      bh[26] = getStateCode(section2.state)    // 26. State Code
      bh[27] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? (section2.pinCode || '') : '' // 27. PIN Code
      bh[28] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? padString(section2.email, 75) : '' // 28. Deductor Email ID
      bh[29] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? (section2.stdCode || '91') : '' // 29. Deductor Contact Country Code
      bh[30] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? (section2.telephoneNo || section2.mobileNo || '') : '' // 30. Deductor Contact Number
      bh[31] = ''                              // 31. Filler 1
      bh[32] = section1.typeOfDeduction || ''  // 32. Deductor Type
      bh[33] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? padString(section3.name, 75) : '' // 33. Resp Person Name
      bh[34] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? padString(section3.designation, 20) : '' // 34. Designation
      bh[35] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? padString(rAddr1, 25) : '' // 35. Resp Person Address 1
      bh[36] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? padString(section3.nameOfPremises || section3.areaLocality || '', 25) : '' // 36. Resp Person Address 2
      bh[37] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? padString(section3.roadStreet || section3.townCity || '', 25) : '' // 37. Resp Person Address 3
      bh[38] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? padString(section3.areaLocality || '', 25) : '' // 38. Resp Person Address 4
      bh[39] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? padString(section3.townCity || '', 25) : '' // 39. Resp Person Address 5
      bh[40] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? getStateCode(section3.state) : '' // 40. Resp Person State Code
      bh[41] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? (section3.pinCode || '') : '' // 41. Resp Person PIN Code
      bh[42] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? padString(section3.email, 75) : '' // 42. Resp Person Email ID
      bh[43] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? padString(section3.countryRegion || 'INDIA', 25) : '' // 43. Resp Person Country Region
      bh[44] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? (section3.stdCode || '91') : '' // 44. Resp Person Contact Country/Region
      bh[45] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? (section3.mobileNo || section3.telephone || '') : '' // 45. Resp Person Contact Number
      bh[46] = ''                              // 46. Filler 3
      bh[47] = (corrType === 'C1' || corrType === 'C5') ? '' : formatDecimalAmount(batchTotalAmt) // 47. Batch Total Deposit Amount
      bh[48] = ''                              // 48. Unmatched challan count
      bh[49] = ''                              // 49. Count of Salary Detail Records
      bh[50] = ''                              // 50. Batch Total Gross Income
      bh[51] = ''                              // 51. Filler 4
      bh[52] = ''                              // 52. Earlier period filed
      const deductorType = String(section1.typeOfDeduction || '').trim().toUpperCase()
      const dTypeCode = deductorType.charAt(0)
      const isStateGovtDeductor = ['S', 'E', 'H', 'N'].includes(dTypeCode)

      bh[53] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? (originalProfileData?.section1?.typeOfDeduction || section1.typeOfDeduction || '') : '' // 53. Last Deductor Type
      bh[54] = (['C1', 'C2', 'C3'].includes(corrType) && isStateGovtDeductor) ? getStateCode(section2.state) : '' // 54. State Name (Mandatory for S, E, H, N; blank for other categories)
      bh[55] = ''                              // 55. Filler 5
      bh[56] = ''                              // 56. Filler 6
      bh[57] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? (section1.ministryName || '') : '' // 57. Ministry Name
      bh[58] = ((updInd === '1' || corrType === 'C1') && corrType !== 'C5' && corrType !== 'C9') ? (section1.ministryNameOther || '') : '' // 58. Ministry Name Others
      bh[59] = section3.permanentAccountNumber || '' // 59. PAN of Responsible Person
      bh[60] = ''                              // 60. Filler 7
      bh[61] = ''                              // 61. Filler 8
      bh[62] = ''                              // 62. Filler 9
      bh[63] = ''                              // 63. Filler 10
      bh[64] = ''                              // 64. Filler 11
      bh[65] = ''                              // 65. Filler 12
      bh[66] = ''                              // 66. Filler 13
      bh[67] = ''                              // 67. Filler 14
      bh[68] = (updInd === '1' && corrType !== 'C5' && corrType !== 'C9') ? (section1.ain || '') : '' // 68. AIN of PAO/TO/CDDO
      bh[69] = section2.gstin || ''            // 69. GSTIN of Deductor
      bh[70] = ''                              // 70. Count of Sec 194P
      bh[71] = ''                              // 71. Gross Total Sec 194P
      bh[72] = ''                              // 72. Record Hash
      return bh
    }

    const globalMaxRecordNo = Math.max(0, ...tdsData
      .filter(t => t.source === 'conso')
      .map(t => parseInt(t.recordNo || 0, 10)))
    let nextAdditionSerial = globalMaxRecordNo + 1

        // Calculate highest challan record number in original conso file
    const maxConsoChallanRecordNo = Math.max(0, ...challanData
      .filter(c => c.source === 'conso' || (c.recordNo && parseInt(c.recordNo, 10) > 0))
      .map(c => parseInt(c.recordNo || 0, 10)))
    let nextAddedChallanNo = maxConsoChallanRecordNo + 1
    const assignedChallanRecordNos = new Map()

    const getChallanRecordNo = (challan) => {
      if (challan.recordNo && parseInt(challan.recordNo, 10) > 0) {
        return parseInt(challan.recordNo, 10)
      }
      if (!assignedChallanRecordNos.has(challan.id)) {
        assignedChallanRecordNos.set(challan.id, nextAddedChallanNo++)
      }
      return assignedChallanRecordNos.get(challan.id)
    }

    // Per-challan counter for additions starting after the last record no of DD in that challan
    const nextDeducteeNoByChallan = {}
    const getNextDeducteeNo = (challanId) => {
      if (nextDeducteeNoByChallan[challanId] === undefined) {
        const existingConsoDDs = tdsData.filter(t => t.challanId == challanId && t.source === 'conso' && t.recordNo)
        const maxNo = Math.max(0, ...existingConsoDDs.map(t => parseInt(t.recordNo, 10)))
        nextDeducteeNoByChallan[challanId] = maxNo + 1
      }
      return nextDeducteeNoByChallan[challanId]++
    }

    // Emit CD + DD records
    const emitCDDD = (challan, dds, batchNo, challanRecordNo, isChallanUpdation, corrType, panUpdatedDeducteeIds) => {
      const cd = Array(31).fill('')
      const isC5 = corrType === 'C5'
      const isC9 = corrType === 'C9'

      // Compute Annexure totals (Field 21 and Field 22)
      let totalAnnexureDeposited = 0
      let totalAnnexureDeducted = 0

      dds.forEach((tds) => {
        const tdsVal = parseFloat(tds.tdsAmount || 0)
        const surchargeVal = parseFloat(tds.surcharge || 0)
        const cessVal = parseFloat(tds.cess || 0)
        const currentTax = tdsVal + surchargeVal + cessVal

        const isNew = tds.source !== 'conso'
        let mode = 'U'
        if (isC9) mode = 'O'
        else if (isNew) mode = 'A'
        else if (tds.consoMode === 'F') mode = 'F'

        const origTotalTax = parseFloat(tds.originalTotalTax !== undefined && tds.originalTotalTax !== '' ? tds.originalTotalTax : currentTax)

        if (mode === 'A' || mode === 'O') {
          totalAnnexureDeposited += currentTax
          totalAnnexureDeducted += currentTax
        } else if (mode === 'U') {
          totalAnnexureDeposited += (currentTax - origTotalTax)
          totalAnnexureDeducted += (currentTax - origTotalTax)
        }
      })

      cd[1] = lineNumber                       // 1. Line Number
      cd[2] = 'CD'                             // 2. Record Type
      cd[3] = String(batchNo)                  // 3. Batch Number
      cd[4] = challanRecordNo                  // 4. Challan Record Number
      cd[5] = (corrType === 'C1' || corrType === 'C2') ? '' : dds.length // 5. Count of Deductee Records
      cd[6] = (parseFloat(challan.totalAmount || 0) === 0) ? 'Y' : 'N' // 6. NIL Challan Indicator
      cd[7] = (isC5 || isC9) ? '' : (isChallanUpdation ? '1' : '0') // 7. Challan Updation Indicator
      cd[8] = isC5 ? '' : (isChallanUpdation || isC9 ? formatDecimalAmount(challan.tdsAmount) : '') // 8. Total Tax Deducted (Income Tax)
      cd[9] = isC5 ? '' : (isChallanUpdation || isC9 ? formatDecimalAmount(challan.interest) : '') // 9. Total Interest
      cd[10] = isC5 ? '' : (isChallanUpdation || isC9 ? formatDecimalAmount(challan.feesAmount) : '0.00') // 10. Total Fee
      cd[11] = isC5 ? '' : (isChallanUpdation || isC9 ? formatDecimalAmount(challan.other) : '') // 11. Total Penalty
      cd[12] = isC5 ? '' : formatDecimalAmount(challan.totalAmount) // 12. Total Deposit Amount
      cd[13] = challan.bookEntry ? 'B' : 'C'   // 13. Mode of payment (C/B)
      cd[14] = isC9 ? '' : (challan.originalBsrCode || challan.bsrCode || '') // 14. Last BSR Code
      cd[15] = isC5 ? '' : (isChallanUpdation || isC9 ? (challan.bsrCode || '') : (challan.originalBsrCode || challan.bsrCode || '')) // 15. BSR Code
      cd[16] = isC9 ? '' : (challan.originalChallanNo || challan.challanSerialNo || '') // 16. Last Bank Challan No
      cd[17] = isC5 ? '' : (isChallanUpdation || isC9 ? (challan.challanSerialNo || '') : (challan.originalChallanNo || challan.challanSerialNo || '')) // 17. Bank Challan No
      cd[18] = isC9 ? '' : formatDate(challan.originalDateOfDeposit || challan.dateOfDeposit) // 18. Last Date of Deposit
      cd[19] = isC5 ? '' : (isChallanUpdation || isC9 ? formatDate(challan.dateOfDeposit) : formatDate(challan.originalDateOfDeposit || challan.dateOfDeposit)) // 19. Date of Deposit
      cd[20] = isC9 ? '' : formatDecimalAmount(challan.originalTotalAmount || challan.totalAmount) // 20. Last Total Deposit Amount
      cd[21] = (isC5 || corrType === 'C1' || corrType === 'C2') ? '' : formatDecimalAmount(totalAnnexureDeposited) // 21. Total Tax Deposited per Annexure
      cd[22] = (isC5 || corrType === 'C1' || corrType === 'C2') ? '' : formatDecimalAmount(totalAnnexureDeducted) // 22. Total Tax Deducted per Annexure
      cd[23] = isC5 ? '' : (isChallanUpdation || isC9 ? formatMinorHead(challan.minorHead) : '') // 23. Minor Head
      cd[24] = isC5 ? '' : formatDecimalAmount(challan.interestAllocation !== undefined && challan.interestAllocation !== '' ? challan.interestAllocation : (challan.interest || 0)) // 24. Interest Allocation
      cd[25] = isC5 ? '' : formatDecimalAmount(challan.otherAllocation !== undefined && challan.otherAllocation !== '' ? challan.otherAllocation : (challan.feesAllocation !== undefined && challan.feesAllocation !== '' ? challan.feesAllocation : (challan.other || challan.penalty || 0))) // 25. Other Allocation
      cd[26] = ''                              // 26. Filler 3
      cd[27] = ''                              // 27. Filler 4
      cd[28] = ''                              // 28. Filler 5
      cd[29] = ''                              // 29. Filler 6
      cd[30] = ''                              // 30. Record Hash
      fileContent += cd.slice(1, 31).join('^') + '\n'
      lineNumber++

      // DD records
      const sortedDds = [...dds].sort((a, b) => {
        const aNo = a.source === 'conso' ? parseInt(a.recordNo || 0, 10) : 999999
        const bNo = b.source === 'conso' ? parseInt(b.recordNo || 0, 10) : 999999
        return aNo - bNo
      })

      const seenRecordNos = new Set()
      sortedDds.forEach((tds) => {
        const dd = Array(46).fill('')
        const tdsVal = parseFloat(tds.tdsAmount || 0)
        const surchargeVal = parseFloat(tds.surcharge || 0)
        const cessVal = parseFloat(tds.cess || 0)
        const totalTax = tdsVal + surchargeVal + cessVal

        const isNew = tds.source !== 'conso'
        let mode = 'U'
        if (isC9) mode = 'O'
        else if (isNew) mode = 'A'
        else if (tds.consoMode === 'F') mode = 'F'
        let recordNo = isNew ? getNextDeducteeNo(challan.id) : (parseInt(tds.recordNo, 10) || tds.recordNo)
        if (!recordNo || seenRecordNos.has(String(recordNo))) {
          recordNo = getNextDeducteeNo(challan.id)
        }
        seenRecordNos.add(String(recordNo))

        // In multi-batch with C5, if this deductee had PAN updated in C5 batch, Last PAN in C3 must equal new PAN
        const wasPanUpdatedInC5 = panUpdatedDeducteeIds && panUpdatedDeducteeIds.has(tds.id)
        const lastPanValue = wasPanUpdatedInC5
          ? String(tds.panOfDeductee || '').trim().toUpperCase()
          : String(tds.originalPan || tds.panOfDeductee || '').trim().toUpperCase()

        dd[1] = lineNumber                   // 1. Line Number
        dd[2] = 'DD'                         // 2. Record Type
        dd[3] = String(batchNo)              // 3. Batch Number
        dd[4] = recordNo                     // 4. Deductee Record Number
        dd[5] = challanRecordNo              // 5. Challan Reference Number
        dd[6] = (isC5 || isC9) ? (isC9 ? 'O' : '') : mode // 6. Mode (U, A, F, O)
        dd[7] = (mode === 'U' && !isC5 && !isC9) ? lastPanValue : '' // 7. Last Deductee PAN
        dd[8] = String(tds.panOfDeductee || '').replace(/[\r\n]/g, '').trim() || 'PANNOTAVBL' // 8. Deductee PAN
        dd[9] = isC5 ? '' : padString(tds.nameOfDeductee || tds.firstName || tds.name || '', 75) // 9. Name of Deductee
        dd[10] = ''                          // 10. Remarks 1
        dd[11] = ''                          // 11. Remarks 2
        dd[12] = ''                          // 12. Remarks 3
        dd[13] = ''                          // 13. Remarks 4
        dd[14] = ''                          // 14. Remarks 5
        dd[15] = isC5 ? '' : (tds.sectionCode || '1024') // 15. Section Code (1004 to 1067)
        dd[16] = ''                          // 16. Remarks 6
        dd[17] = ''                          // 17. Remarks 7
        dd[18] = isC5 ? '' : 'Y'             // 18. Tax Deposited Yes/No
        dd[19] = isC5 ? '' : formatDate(tds.dateOfPayment) // 19. Date of payment / credited
        dd[20] = isC5 ? '' : formatDecimalAmount(tds.amountOfPayment) // 20. Amount Paid / Credited
        dd[21] = ''                          // 21. Grossing up Indicator
        dd[22] = isC5 ? '' : (tds.cashWithdrawal1Cr ? formatDecimalAmount(tds.cashWithdrawal1Cr) : '') // 22. Cash withdrawal > 1 Cr
        dd[23] = isC5 ? '' : (tds.cashWithdrawal3Cr ? formatDecimalAmount(tds.cashWithdrawal3Cr) : '') // 23. Cash withdrawal > 3 Cr
        const originalTds = getOriginalDeductee(tds)

        const getOrigTax = (item) => {
          if (!item) return 0
          if (item.originalTotalTax !== undefined && item.originalTotalTax !== null && item.originalTotalTax !== '' && !isNaN(Number(item.originalTotalTax))) {
            return parseFloat(item.originalTotalTax)
          }
          const t = parseFloat(item.tdsAmount || 0)
          const s = parseFloat(item.surcharge || 0)
          const c = parseFloat(item.cess || 0)
          return t + s + c
        }

        const getOrigTaxDeposited = (item) => {
          if (!item) return 0
          if (item.originalTotalTaxDeposited !== undefined && item.originalTotalTaxDeposited !== null && item.originalTotalTaxDeposited !== '' && !isNaN(Number(item.originalTotalTaxDeposited))) {
            return parseFloat(item.originalTotalTaxDeposited)
          }
          return getOrigTax(item)
        }

        const lastTotalTax = originalTds ? getOrigTax(originalTds) : getOrigTax(tds)
        const lastTotalTaxDeposited = originalTds ? getOrigTaxDeposited(originalTds) : getOrigTaxDeposited(tds)

        dd[24] = isC5 ? '' : formatDecimalAmount(totalTax) // 24. Total Tax Deducted
        dd[25] = isC5 ? '' : formatDecimalAmount(totalTax) // 25. Total Tax Deposited
        dd[26] = (mode === 'U' || isC5) ? formatDecimalAmount(lastTotalTax) : '' // 26. Last total tax deducted (before updation)
        dd[27] = isC5 ? '' : (totalTax > 0 ? formatDate(tds.dateOfDeduction) : '') // 27. Date of deduction
        dd[28] = isC5 ? '' : formatRate4Decimals(tds.rateOfTax || tds.rate || 0) // 28. Rate at which tax deducted (4 decimals)
        dd[29] = ''                          // 29. Remarks 9
        dd[30] = ''                          // 30. Remarks 10
        dd[31] = ''                          // 31. Filler / Blank (Ref: Form 140 Spec)
        dd[32] = isC5 ? '' : (tds.reasonForNonDeduction || '') // 32. Reason for non-deduction
        dd[33] = isC5 ? '' : (tds.certificateNo197 || '') // 33. Certificate Number u/s 395/197
        dd[34] = ''                          // 34. Filler 1
        dd[35] = ''                          // 35. Filler 2
        dd[36] = ''                          // 36. Filler 3
        dd[37] = ''                          // 37. Filler 4
        dd[38] = ''                          // 38. Filler 5
        dd[39] = ''                          // 39. Filler 6
        dd[40] = ''                          // 40. Filler 7
        dd[41] = ''                          // 41. Filler 8
        dd[42] = (mode === 'U' || isC5) ? formatDecimalAmount(lastTotalTaxDeposited) : '' // 42. Last Total Tax Deposited (before updation)
        dd[43] = ''                          // 43. Filler 10
        dd[44] = ''                          // 44. Filler 11
        dd[45] = ''                          // 45. Record Hash
        fileContent += dd.slice(1, 46).join('^') + '\n'
        lineNumber++
      })
    }

    // Set of deductee IDs that had PAN updated in C5 batch
    const panUpdatedIds = new Set()
    c5Groups.forEach(g => {
      g.panDDs.forEach(d => panUpdatedIds.add(d.id))
    })

    // Process all batches
    batches.forEach((batch, batchIdx) => {
      const currentBatchNo = batchIdx + 1
      const corrType = batch.type
      const includesBhChanges = isBhChanged()

      let batchTotalAmt = 0
      batch.groups.forEach(g => {
        batchTotalAmt += parseFloat(g.challan.totalAmount || 0)
      })

      const bhRecord = buildBH(currentBatchNo, corrType, includesBhChanges, batchTotalAmt)
      bhRecord[4] = (corrType === 'C1') ? '' : String(batch.groups.length)
      fileContent += bhRecord.slice(1, 73).join('^') + '\n'
      lineNumber++

      batch.groups.forEach((group, challanIdx) => {
        const challanRecordNo = getChallanRecordNo(group.challan)
        let ddsToEmit = []
        if (corrType === 'C5') {
          ddsToEmit = group.panDDs
        } else if (corrType === 'C9') {
          ddsToEmit = group.allDDs
        } else if (corrType === 'C3') {
          ddsToEmit = group.changedDDs || group.otherDDs
        } else {
          ddsToEmit = []
        }
        emitCDDD(group.challan, ddsToEmit, currentBatchNo, challanRecordNo, group.challanChanged, corrType, panUpdatedIds)
      })
    })

    return fileContent
  }

  // Conso File Parsing Logic for Form 140
  const parseConsoFile = (fileContent) => {
    const lines = fileContent.split('\n')

    // Validate BH record form type
    const bhLine = lines.find(l => {
      const p = l.split('^')
      return p[1] === 'BH'
    })

    if (bhLine) {
      const bhParts = bhLine.split('^')
      const formInConso = (bhParts[4] || '').toUpperCase().trim()
      if (formInConso && formInConso !== '140' && formInConso !== '26Q') {
        toast.error(`Invalid Conso File! Expected Form 140, but file contains Form ${formInConso}.`)
        alert(`Invalid Conso File!\n\nThis form is Form 140, but the uploaded Conso file belongs to Form ${formInConso}.\nPlease upload the correct Form 140 Conso file.`)
        return
      }
    }

    setConsoFileContent(fileContent)
    const newChallans = []
    const newTdsData = []
    let currentChallan = null
    let rowIndex = 0

    try {
      lines.forEach((line) => {
        if (!line.trim()) return
        const parts = line.split('^')
        const recordType = parts[1]

        if (recordType === 'FH') {
          const hashVal = parts[14] || parts[15] || parts[18] || parts[17] || ''
          if (hashVal) {
            setConsoFhHash(hashVal)
          }
        } else if (recordType === 'BH') {
          const stateCodeMap = {
            '01': 'ANDAMAN AND NICOBAR ISLANDS', '02': 'ANDHRA PRADESH', '03': 'ARUNACHAL PRADESH', '04': 'ASSAM', '05': 'BIHAR',
            '06': 'CHANDIGARH', '07': 'DADRA AND NAGAR HAVELI', '08': 'DAMAN AND DIU', '09': 'DELHI', '10': 'GOA',
            '11': 'GUJARAT', '12': 'HARYANA', '13': 'HIMACHAL PRADESH', '14': 'JAMMU AND KASHMIR', '15': 'KARNATAKA',
            '16': 'KERALA', '17': 'LAKSHADWEEP', '18': 'MADHYA PRADESH', '19': 'MAHARASHTRA', '20': 'MANIPUR',
            '21': 'MEGHALAYA', '22': 'MIZORAM', '23': 'NAGALAND', '24': 'ORISSA', '25': 'PONDICHERRY',
            '26': 'PUNJAB', '27': 'RAJASTHAN', '28': 'SIKKIM', '29': 'TAMIL NADU', '30': 'TRIPURA',
            '31': 'UTTAR PRADESH', '32': 'WEST BENGAL', '33': 'CHHATTISGARH', '34': 'UTTARANCHAL', '35': 'JHARKHAND',
            '36': 'TELANGANA', '37': 'LADAKH', '99': 'FOREIGN'
          }

          const formType = parts[4]
          const fyRaw = parts[16]
          const quarter = parts[17]

          if (formType) {
            setSelectedForm('140')
            localStorage.setItem('tds140Form', '140')
          }
          if (quarter) {
            setSelectedQuarter(quarter)
            localStorage.setItem('tds140Quarter', quarter)
          }
          if (fyRaw && fyRaw.length === 6) {
            const formattedFy = `${fyRaw.slice(0, 4)}-${fyRaw.slice(4, 6)}`
            setSelectedFinancialYear(formattedFy)
            localStorage.setItem('tds140FinancialYear', formattedFy)
          }

          const getMappedState = (code) => {
            if (!code) return '';
            const padded = code.padStart(2, '0');
            return stateCodeMap[padded] || code || '';
          };

          const pData = {
            section1: {
              taxDeduction: parts[12] || '',
              permanentAccountNumber: parts[14] || '',
              typeOfDeduction: parts[31] || '',
              receiptNoOriginal: parts[7] || '',
              receiptNoEarlier: parts[8] || ''
            },
            section2: {
              name: parts[18] || '',
              branchDivision: parts[19] || '',
              flatNo: parts[20] || '',
              nameOfPremises: parts[21] || '',
              roadStreet: parts[22] || '',
              areaLocality: parts[23] || '',
              townCity: parts[24] || '',
              state: getMappedState(parts[25]),
              pinCode: parts[26] || '',
              email: parts[27] || '',
              stdCode: parts[28] || '',
              telephoneNo: parts[29] || '',
              gstin: parts[68] || ''
            },
            section3: {
              name: parts[32] || '',
              designation: parts[33] || '',
              flatNo: parts[34] || '',
              nameOfPremises: parts[35] || '',
              roadStreet: parts[36] || '',
              areaLocality: parts[37] || '',
              townCity: parts[38] || '',
              state: getMappedState(parts[39]),
              pinCode: parts[40] || '',
              email: parts[41] || '',
              mobileNo: parts[47] || parts[44] || '',
              telephone: parts[44] || '',
              telephoneAlternate: parts[45] || '',
              permanentAccountNumber: parts[58] || '',
              hasAddressChanged: parts[46] === 'Y' ? 'Yes' : 'No'
            }
          }

          setProfileData(pData)
          setOriginalProfileData(JSON.parse(JSON.stringify(pData)))
          setStorageItem('tds140OriginalProfileData', pData)
        } else if (recordType === 'CD') {
          const challan = {
            id: Date.now() + rowIndex++,
            challanSerialNo: parts[11] || parts[16] || '',
            originalChallanNo: parts[11] || parts[16] || '',
            bsrCode: parts[15] || parts[14] || '',
            originalBsrCode: parts[15] || parts[14] || '',
            dateOfDeposit: parts[17] ? `${parts[17].slice(4, 8)}-${parts[17].slice(2, 4)}-${parts[17].slice(0, 2)}` : '',
            originalDateOfDeposit: parts[17] ? `${parts[17].slice(4, 8)}-${parts[17].slice(2, 4)}-${parts[17].slice(0, 2)}` : '',
            minorHead: parts[40] || parts[23] || '200',
            originalMinorHead: parts[40] || parts[23] || '200',
            sectionCode: '',
            tdsAmount: parseFloat(parts[21] || parts[8] || 0),
            originalTdsAmount: parseFloat(parts[21] || parts[8] || 0),
            surcharge: parseFloat(parts[22] || 0),
            educationCess: parseFloat(parts[23] || 0),
            interest: parseFloat(parts[24] || parts[9] || 0),
            originalInterest: parseFloat(parts[24] || parts[9] || 0),
            other: parseFloat(parts[25] || parts[11] || 0),
            originalOther: parseFloat(parts[25] || parts[11] || 0),
            totalAmount: parseFloat(parts[26] || parts[12] || 0),
            originalTotalAmount: parseFloat(parts[26] || parts[12] || 0),
            feesAmount: parseFloat(parts[38] || parts[10] || 0),
            originalFeesAmount: parseFloat(parts[38] || parts[10] || 0),
            interestAllocation: parseFloat(parts[33] !== undefined && parts[33] !== '' ? parts[33] : (parts[24] || parts[9] || 0)),
            originalInterestAllocation: parseFloat(parts[33] !== undefined && parts[33] !== '' ? parts[33] : (parts[24] || parts[9] || 0)),
            feesAllocation: parseFloat(parts[34] !== undefined && parts[34] !== '' ? parts[34] : (parts[38] || parts[25] || parts[11] || 0)),
            originalFeesAllocation: parseFloat(parts[34] !== undefined && parts[34] !== '' ? parts[34] : (parts[38] || parts[25] || parts[11] || 0)),
            otherAllocation: parseFloat(parts[34] !== undefined && parts[34] !== '' ? parts[34] : (parts[38] || parts[25] || parts[11] || 0)),
            originalOtherAllocation: parseFloat(parts[34] !== undefined && parts[34] !== '' ? parts[34] : (parts[38] || parts[25] || parts[11] || 0)),
            bookEntry: parts[36] === 'Y' || parts[13] === 'B',
            originalBookEntry: parts[36] === 'Y' || parts[13] === 'B',
            recordNo: parts[3] ? parseInt(parts[3], 10) : '',
            source: 'conso',
            source: 'conso',
            higherEducationCess: 0
          }
          currentChallan = challan
          newChallans.push(challan)

        } else if (recordType === 'DD') {
          if (!currentChallan) return

          const extractDate = (...candidates) => {
            for (const cand of candidates) {
              if (!cand) continue;
              const str = String(cand).trim();
              if (/^\d{8}$/.test(str)) {
                return `${str.slice(4, 8)}-${str.slice(2, 4)}-${str.slice(0, 2)}`;
              }
              if (/^\d{4}-\d{2}-\d{2}$/.test(str) || /^\d{2}-\d{2}-\d{4}$/.test(str)) {
                return str;
              }
            }
            return '';
          };

          const extractNumber = (...candidates) => {
            for (const cand of candidates) {
              if (cand === undefined || cand === null || cand === '') continue;
              const num = parseFloat(cand);
              if (!isNaN(num) && num > 0) return num;
            }
            return 0;
          };

          const pan = (parts[9] || parts[8] || parts[7] || '').trim();
          let fullName = (parts[12] || parts[9] || parts[8] || '').trim();
          if (fullName === pan && parts[12]) fullName = parts[12].trim();

          const paymentAmt = extractNumber(parts[21], parts[20], parts[19]);
          const rawRate = extractNumber(parts[28], parts[25]);
          const ratePercent = rawRate > 0 && rawRate < 1 ? rawRate * 100 : rawRate;

          let tdsVal = extractNumber(parts[16], parts[18], parts[24], parts[25], parts[10], parts[11], parts[12], parts[15], parts[13]);
          if (tdsVal === 0 && paymentAmt > 0 && ratePercent > 0) {
            tdsVal = Math.round(((paymentAmt * ratePercent) / 100) * 100) / 100;
          }

          const payDate = extractDate(parts[22], parts[19], parts[23], parts[27]);
          const dedDate = extractDate(parts[23], parts[27], parts[22], parts[19]);
          const secCode = (parts[33] || parts[15] || '1024').trim();

          const deductee = {
            id: Date.now() + rowIndex++,
            challanId: currentChallan.id,
            deducteeCode: parts[6] === '1' || parts[7] === '1' ? '1' : '2',
            panOfDeductee: pan,
            originalPan: pan,
            firstName: fullName,
            middleName: '',
            lastName: '',
            nameOfDeductee: fullName,
            amountOfPayment: paymentAmt,
            dateOfPayment: payDate,
            dateOfDeduction: dedDate,
            rateOfTax: ratePercent.toFixed(4),
            tdsAmount: tdsVal,
            originalTotalTax: tdsVal,
            originalTotalTaxDeposited: tdsVal,
            surcharge: parseFloat(parts[14] || 0),
            cess: parseFloat(parts[15] || 0),
            sectionCode: secCode,
            reasonForNonDeduction: parts[29] || '',
            certificateNo197: parts[34] || '',
            recordNo: (parts[4] && parseInt(parts[4], 10) > 0) ? parseInt(parts[4], 10) : (parts[3] && parseInt(parts[3], 10) > 0 && parts[3] !== '1' ? parseInt(parts[3], 10) : rowIndex),
            challanRecordNo: parts[3] ? parseInt(parts[3], 10) : 1,
            source: 'conso',
            consoMode: parts[6] || parts[5] || 'F'
          }
          newTdsData.push(deductee)
        }
      })

      newChallans.forEach(challan => {
        const matchingDeductee = newTdsData.find(tds => tds.challanId === challan.id)
        if (matchingDeductee && matchingDeductee.sectionCode) {
          if (!challan.sectionCode) {
            challan.sectionCode = matchingDeductee.sectionCode
          }
        }
      })

      setChallanData(newChallans)
      setTdsData(newTdsData)
      setOriginalChallanData(JSON.parse(JSON.stringify(newChallans)))
      setOriginalTdsData(JSON.parse(JSON.stringify(newTdsData)))
      setStorageItem('tds140OriginalChallanData', newChallans)
      toast.success('Form 140 Conso File Uploaded and Parsed Successfully!')

    } catch (err) {
      console.error(err)
      toast.error('Error parsing Form 140 conso file')
    }
  }

  const handleConsoFileUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      setConsoFileContent(ev.target.result)
      parseConsoFile(ev.target.result)
    }
    reader.readAsText(file)
  }

  useEffect(() => {
    if (selectedReturnType === 'Regular') {
      navigate('/tds-dashboard/140');
    }
  }, [selectedReturnType, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 w-full overflow-x-hidden">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* CSI Upload Modal Popup */}
      {showCsiUploadPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Upload CSI File</h3>
            <p className="text-xs text-gray-600 mb-4">Select .csi file from Income Tax Department portal to proceed with validation.</p>
            <input
              type="file"
              ref={csiFileInputRef}
              accept=".csi"
              onChange={(e) => {
                const file = e.target.files[0]
                if (file) {
                  setCsiFile(file)
                  setShowCsiUploadPopup(false)
                  executeValidation(file)
                }
              }}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCsiUploadPopup(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSI Password Download Modal */}
      {showDownloadPrompt && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md transform transition-all scale-100 relative">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <Upload className="w-5 h-5 mr-2 text-blue-600" />
              Download CSI File
            </h3>

            <p className="text-gray-600 mb-4 text-sm">
              Would you like to automatically download the latest CSI file from the Income Tax Portal?
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Income Tax Password</label>
              <div className="relative">
                <input
                  type={showDownloadPassword ? "text" : "password"}
                  className="w-full border border-gray-300 rounded-md p-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white text-gray-900"
                  value={downloadPassword}
                  onChange={(e) => setDownloadPassword(e.target.value)}
                  placeholder="Enter Password"
                />
                <button
                  type="button"
                  onClick={() => setShowDownloadPassword(!showDownloadPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                >
                  {showDownloadPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">TAN: {profileData?.section1?.taxDeduction}</p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleCsiDownload}
                disabled={isDownloadingCsi}
                className={`flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition flex items-center justify-center border border-transparent shadow-md ${isDownloadingCsi ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isDownloadingCsi ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Downloading...
                  </>
                ) : (
                  <span className="font-medium">Download & Continue</span>
                )}
              </button>
              <button
                onClick={handleSkipDownload}
                disabled={isDownloadingCsi}
                className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition font-medium"
              >
                Skip (I have file)
              </button>
            </div>
            <button
              onClick={() => setShowDownloadPrompt(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Top Banner Header */}
      <header className={`bg-white border-b border-gray-200 transition-all duration-300 ${!isMobile ? (sidebarState.isExpanded ? 'ml-64' : 'ml-14') : ''
        }`} style={{
          width: !isMobile ? (sidebarState.isExpanded ? 'calc(100% - 16rem)' : 'calc(100% - 3.5rem)') : '100%'
        }}>
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/return-selection')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Building2 className="w-6 h-6 text-blue-600" />
                Form 140 Correction (Non-Salary TDS - FY 2026-27+)
              </h1>
              <p className="text-xs text-gray-500">Prepare Revised / Correction Non-Salary TDS Return File according to Income Tax Department specs</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="cursor-pointer bg-blue-50 text-blue-700 hover:bg-blue-100 px-4 py-2 rounded-md text-sm font-medium border border-blue-200 flex items-center gap-2 transition-colors">
              <Upload className="w-4 h-4" />
              Upload Conso File (.conso, .tds)
              <input
                type="file"
                accept=".conso,.tds,.txt"
                onChange={handleConsoFileUpload}
                className="hidden"
              />
            </label>

            <button
              onClick={handlePrepareReturnFile}
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-md text-sm font-semibold shadow-sm transition-colors flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Prepare Correction Return
            </button>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <TdsSidebar
        isOpen={true}
        isMobile={isMobile}
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        onClose={handleSidebarClose}
        onSidebarStateChange={handleSidebarStateChange}
        tdsData={tdsData}
        challanData={challanData}
        formType="140"
        isCorrection={true}
        selectionComplete={true}
      />

      {/* Main Content Layout */}
      <main className={`py-6 px-6 transition-all duration-300 space-y-6 ${!isMobile ? (sidebarState.isExpanded ? 'ml-64' : 'ml-14') : ''
        }`} style={{
          width: !isMobile ? (sidebarState.isExpanded ? 'calc(100% - 16rem)' : 'calc(100% - 3.5rem)') : '100%',
          maxWidth: '100%',
          overflowX: 'hidden'
        }}>

        {/* FY & Quarter Selection Bar */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-4 items-center">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Financial Year</label>
              <select
                value={selectedFinancialYear || '2026-27'}
                onChange={(e) => setSelectedFinancialYear(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium bg-gray-50"
              >
                <option value="2026-27">2026-27</option>
                <option value="2027-28">2027-28</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Quarter</label>
              <select
                value={selectedQuarter || 'Q1'}
                onChange={(e) => setSelectedQuarter(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium bg-gray-50"
              >
                <option value="Q1">Q1</option>
                <option value="Q2">Q2</option>
                <option value="Q3">Q3</option>
                <option value="Q4">Q4</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Form Type</label>
              <select
                value="140"
                onChange={(e) => {
                  const targetPath = getTdsFormRoute(e.target.value, 'Correction', selectedFinancialYear)
                  navigate(targetPath)
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium bg-gray-50 cursor-pointer"
              >
                <option value="138">138 (Salary)</option>
                <option value="140">140 (Non-Salary)</option>
                <option value="143">143 (TCS)</option>
                <option value="144">144 (Non-Resident)</option>
              </select>
            </div>

            <div className="flex items-center gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Return Type</label>
                <select
                  value="Correction"
                  onChange={(e) => {
                    const targetPath = getTdsFormRoute('140', e.target.value, selectedFinancialYear)
                    navigate(targetPath)
                  }}
                  className="px-3 py-1.5 border border-amber-300 rounded-md text-sm font-medium bg-amber-50 text-amber-900 cursor-pointer"
                >
                  <option value="Regular">Regular</option>
                  <option value="Correction">Correction (Revised)</option>
                </select>
              </div>
            </div>
          </div>

          {profileData?.section1?.receiptNoOriginal && (
            <div className="text-right text-xs text-gray-500">
              <div>Original PRN: <span className="font-semibold text-gray-800">{profileData.section1.receiptNoOriginal}</span></div>
              <div>Previous PRN: <span className="font-semibold text-gray-800">{profileData.section1.receiptNoEarlier || profileData.section1.receiptNoOriginal}</span></div>
            </div>
          )}
        </div>

        {/* Section Content */}
        <div className="space-y-6">
          {activeSection === 'profile' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <TdsProfileSection
                profileData={profileData}
                isProfileEditing={isProfileEditing}
                setIsProfileEditing={setIsProfileEditing}
                onProfileDataChange={handleProfileInputChange}
                showPrefillPrompt={showPrefillPrompt}
                setShowPrefillPrompt={setShowPrefillPrompt}
                handleProfilePrefill={handleProfilePrefill}
                prefillPassword={prefillPassword}
                setPrefillPassword={setPrefillPassword}
                showPrefillPassword={showPrefillPassword}
                setShowPrefillPassword={setShowPrefillPassword}
                isExtractingProfile={isExtractingProfile}
                stateOptions={stateOptions}
                deductorTypes={deductorTypes140}
                formType="140"
                isCorrection={true}
              />
            </div>
          )}

          {activeSection === 'challan-details' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center justify-between">
                <span>Challan Details</span>
                <span className="text-xs font-normal text-gray-500">Total Challans: {challanData?.length || 0}</span>
              </h2>
              <ChallanDetailsTable
                challanData={challanData}
                tdsData={tdsData}
                originalChallanData={originalChallanData}
                onChallanDataChange={handleChallanDataChange}
                sectionOptions={form140Sections}
                minorHeadOptions={minorHeadCodes140}
                formType="140"
                isCorrection={true}
              />
            </div>
          )}

          {activeSection === 'tds-details' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center justify-between">
                <span>Deductee Details</span>
                <span className="text-xs font-normal text-gray-500">Total Deductees: {tdsData?.length || 0}</span>
              </h2>
              <TdsDetailsTable
                tdsData={tdsData}
                originalData={originalTdsData}
                onTdsDataChange={handleTdsDataChange}
                challanData={challanData}
                sectionOptions={form140Sections}
                deducteeCodes={deducteeCodes140}
                formType="140"
                isCorrection={true}
              />
            </div>
          )}

          {activeSection === 'generated-files' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Generated Files & Validation</h2>
              <TdsGeneratedFiles form="140" isEmbedded={true} />
            </div>
          )}
        </div>

      </main>

      {/* Validation Lock Screen Overlay */}
      {isValidating && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gray-900 bg-opacity-80 backdrop-blur-sm cursor-wait">
          <div className="bg-white p-8 rounded-2xl shadow-2xl flex flex-col items-center max-w-lg text-center transform transition-all scale-100">
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-orange-100 rounded-full animate-ping opacity-75"></div>
              <div className="relative bg-white rounded-full p-2">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-200 border-t-orange-600"></div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-gray-800 mb-2">Validation in Progress</h3>
            <p className="text-gray-600 mb-6">
              Please wait while we validate your return.
            </p>
            <button
              onClick={handleCancelValidation}
              className="px-6 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium transition-colors border border-red-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

    </div>
  )
}

export default Tds140CorrectionForm
